import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Trash2, ArrowUp, ArrowDown, Settings2, PinOff, Pin, Eye, Type as TypeIcon, Plus, Minus } from 'lucide-react';
import { CustomNumberInput } from './CustomNumberInput';
import { CanvasElement, Condition, ListColumn } from './types';
import { ConditionsModal } from './ConditionsModal';
import { ListColumnsModal } from './ListColumnsModal';
import { ConditionalYModal } from './ConditionalYModal';

interface RightPanelProps {
  selectedElement: CanvasElement | undefined;
  updateElement: (id: string, updates: Partial<CanvasElement>) => void;
  deleteElement: (id: string) => void;
  variables: any[];
  moveElementLayer?: (id: string, dir: 'up' | 'down') => void;
  isPinned?: boolean;
  onTogglePin?: () => void;
  elements?: CanvasElement[];
  setSelectedElementId?: (id: string | null) => void;
}

export const RightPanel: React.FC<RightPanelProps> = ({
  selectedElement,
  updateElement,
  deleteElement,
  variables,
  moveElementLayer,
  isPinned = false,
  onTogglePin,
  elements = [],
  setSelectedElementId
}) => {
  const [showConditionsModal, setShowConditionsModal] = React.useState(false);
  const [showListColumnsModal, setShowListColumnsModal] = React.useState(false);
  const [showConditionalYModal, setShowConditionalYModal] = React.useState(false);

  if (!selectedElement) {
    return (
      <div className={`w-full h-full bg-card rounded-lg border border-borderColor shadow-sm overflow-hidden transition-all duration-300 relative group`}>
        {onTogglePin && (
          <div className="absolute top-3 right-4 z-10">
            <button
              onClick={onTogglePin}
              className={`p-1.5 rounded hover:bg-bgAlt transition-colors ${isPinned ? 'text-text hover:text-text/70' : 'text-text/50 hover:text-text'}`}
              title={isPinned ? 'Sabitlemeyi Kaldır' : 'Sabitle'}
            >
              {isPinned ? <PinOff className="w-5 h-5" /> : <Pin className="w-5 h-5" />}
            </button>
          </div>
        )}

        <div className={`absolute top-0 right-0 w-16 h-full flex flex-col items-center py-4 gap-4 text-text/50 transition-opacity duration-300 pointer-events-none ${isPinned ? 'opacity-0' : 'opacity-100 group-hover:opacity-0'}`}>
          <Settings2 className="w-6 h-6 shrink-0 mt-12" />
          <span className="font-bold tracking-[0.3em] uppercase text-sm" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            Özellikler
          </span>
        </div>

        {/* Expanded View */}
        <div className={`w-full h-full flex flex-col p-4 transition-opacity duration-300 ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
          <div className="flex items-center w-full mb-4 pr-10">
            <h2 className="text-sm font-bold text-text uppercase tracking-wider">Tuvaldeki Katmanlar</h2>
          </div>
          {elements.length === 0 ? (
            <div className="flex flex-col items-center">
              <Settings2 className="w-6 h-6 text-text/50 mt-4 shrink-0" />
              <p className="text-text/50 text-sm mt-4 whitespace-nowrap text-center">
                Henüz nesne eklenmedi.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2 overflow-y-auto">
              {elements.map((el, i) => (
                <button
                  key={el.id}
                  onClick={() => setSelectedElementId?.(el.id)}
                  className="flex items-center justify-between p-2 rounded-md bg-bgAlt border border-borderColor hover:bg-primary/10 hover:border-primary/30 transition-colors text-left"
                >
                  <span className="text-sm font-medium text-text">
                    {el.type} {el.content && el.type !== 'BOX' && el.type !== 'LINE' ? `(${el.content.substring(0,10)})` : ''}
                  </span>
                  <span className="text-[10px] text-text/50">Katman {i + 1}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full bg-card rounded-lg border border-borderColor shadow-sm overflow-hidden transition-all duration-300 relative group`}>
      {onTogglePin && (
        <div className="absolute top-3 right-3 z-20">
          <button
            onClick={onTogglePin}
            className={`p-1.5 rounded hover:bg-bgAlt transition-colors ${isPinned ? 'text-text hover:text-text/70' : 'text-text/50 hover:text-text'}`}
            title={isPinned ? 'Sabitlemeyi Kaldır' : 'Sabitle'}
          >
            {isPinned ? <PinOff className="w-5 h-5" /> : <Pin className="w-5 h-5" />}
          </button>
        </div>
      )}

      {/* Collapsed View */}
      <div className={`absolute top-0 right-0 w-16 h-full flex flex-col items-center py-4 gap-4 text-text/50 transition-opacity duration-300 pointer-events-none ${isPinned ? 'opacity-0' : 'opacity-100 group-hover:opacity-0'}`}>
        <Settings2 className="w-6 h-6 shrink-0 mt-12" />
        <span className="font-bold tracking-[0.3em] uppercase text-sm" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
          Özellikler
        </span>
      </div>

      {/* Expanded View */}
      <div className={`w-full h-full flex flex-col pt-4 transition-opacity duration-300 ${isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        <div className="flex items-center px-4 mb-4 border-b border-borderColor pb-2 pr-10 shrink-0">
          <div className="flex items-center gap-3">
            <Settings2 className="w-5 h-5 text-text shrink-0" />
            <h2 className="text-sm font-bold text-text uppercase tracking-wider whitespace-nowrap">Özellikler</h2>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded font-mono shrink-0">
              {selectedElement.type}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-4 flex-1 overflow-y-auto px-4 pb-4 custom-scrollbar">
          {/* Content Field (Gizli if BOX/LINE) */}
          {selectedElement.type !== 'BOX' && selectedElement.type !== 'LINE' && (
            <div className="flex flex-col gap-1.5 pr-4">
              <label className="text-xs font-semibold text-text/70">İçerik / Değer</label>
              <InputTextarea
                value={selectedElement.content}
                onChange={(e) => updateElement(selectedElement.id, { content: e.target.value })}
                className="w-full p-inputtext-sm text-sm p-2 bg-bgAlt border-borderColor focus:border-primary text-text"
                rows={3}
                autoResize
              />

              <div className="flex flex-col gap-1.5 mt-2">
                <label className="text-xs font-semibold text-text/70">Örnek Görünüm (Sadece Tasarım)</label>
                <InputTextarea
                  value={selectedElement.sampleValue || ''}
                  onChange={(e) => updateElement(selectedElement.id, { sampleValue: e.target.value })}
                  className="w-full p-inputtext-sm text-sm p-2 bg-bgAlt border-borderColor focus:border-primary text-text"
                  rows={1}
                  placeholder="Örn: Elma 1 Kg"
                  autoResize
                />
              </div>

              <div className="flex flex-wrap gap-1 mt-2">
                {variables?.map((v, i) => {
                  const varStr = `{{${v.key || v.name || v}}}`;
                  const isAdded = selectedElement.content?.includes(varStr);
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        if (isAdded) {
                          updateElement(selectedElement.id, { content: (selectedElement.content || '').replace(varStr, '').replace(/\s{2,}/g, ' ').trim() });
                        } else {
                          updateElement(selectedElement.id, { content: (selectedElement.content || '') + (selectedElement.content ? ' ' : '') + varStr });
                        }
                      }}
                      className={`text-[10px] px-2 py-0.5 rounded border transition-colors cursor-pointer ${isAdded
                        ? 'bg-primary text-white border-primary hover:bg-primaryHover'
                        : 'bg-card text-text border-borderColor hover:bg-bgAlt'
                        }`}
                      title={isAdded ? "Tıklayarak kaldırın" : "Tıklayarak ekleyin"}
                    >
                      {isAdded ? '✓ ' : '+ '}{v.label || v.name || v}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Coordinates */}
          <div className="grid grid-cols-2 gap-3 pr-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-text/70">X Konumu (mm)</label>
              <CustomNumberInput
                value={selectedElement.x}
                onChange={(val) => updateElement(selectedElement.id, { x: val || 0 })}



              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-text/70">Y Konumu (mm)</label>
              <CustomNumberInput
                value={selectedElement.y}
                onChange={(val) => updateElement(selectedElement.id, { y: val || 0 })}
              />
            </div>

            {/* Width & Height */}
            <div className="flex flex-col gap-1.5 mt-2">
              <label className="text-xs font-semibold text-text/70">Genişlik (mm)</label>
              <CustomNumberInput
                value={selectedElement.width || 0}
                onChange={(val) => updateElement(selectedElement.id, { width: val || undefined })}
              />
            </div>
            <div className="flex flex-col gap-1.5 mt-2">
              <label className="text-xs font-semibold text-text/70">Yükseklik (mm)</label>
              <CustomNumberInput
                value={selectedElement.height || 0}
                onChange={(val) => updateElement(selectedElement.id, { height: val || undefined })}
              />
            </div>
          </div>

          {/* Text Properties */}
          {(selectedElement.type === 'TEXT' || selectedElement.type === 'LIST') && (
            <div className="pr-4">
              <div className="flex flex-col gap-1.5 mt-2">
                <label className="text-xs font-semibold text-text/70">Yazı Tipi Boyutu (pt)</label>
                <CustomNumberInput
                  value={selectedElement.fontSize || 12}
                  onChange={(val) => updateElement(selectedElement.id, { fontSize: val || 12 })}
                />
              </div>
              <div className="flex items-center justify-between bg-bgAlt p-2 rounded border border-borderColor mt-2">
                <label className="text-sm font-medium text-text">Kalın Yazı (Bold)</label>
                <InputSwitch
                  checked={selectedElement.fontWeight === 'bold'}
                  onChange={(e) => updateElement(selectedElement.id, { fontWeight: e.value ? 'bold' : 'normal' })}
                />
              </div>

              {selectedElement.type === 'TEXT' && (
                <>
                  <div className="flex flex-col gap-1.5 mt-3">
                    <label className="text-xs font-semibold text-text/70">Yatay Hizalama</label>
                    <div className="flex border border-borderColor rounded-md overflow-hidden bg-bgAlt">
                      <button
                        onClick={() => updateElement(selectedElement.id, { textAlign: 'left' })}
                        className={`flex-1 p-1.5 flex justify-center ${(!selectedElement.textAlign || selectedElement.textAlign === 'left') ? 'bg-primary/10 text-primary' : 'text-text/50 hover:bg-bg'}`}
                      >Sol</button>
                      <button
                        onClick={() => updateElement(selectedElement.id, { textAlign: 'center' })}
                        className={`flex-1 p-1.5 flex justify-center border-l border-r border-borderColor ${selectedElement.textAlign === 'center' ? 'bg-primary/10 text-primary' : 'text-text/50 hover:bg-bg'}`}
                      >Orta</button>
                      <button
                        onClick={() => updateElement(selectedElement.id, { textAlign: 'right' })}
                        className={`flex-1 p-1.5 flex justify-center ${selectedElement.textAlign === 'right' ? 'bg-primary/10 text-primary' : 'text-text/50 hover:bg-bg'}`}
                      >Sağ</button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 mt-3">
                    <label className="text-xs font-semibold text-text/70">Dikey Hizalama</label>
                    <div className="flex border border-borderColor rounded-md overflow-hidden bg-bgAlt">
                      <button
                        onClick={() => updateElement(selectedElement.id, { verticalAlign: 'top' })}
                        className={`flex-1 p-1.5 flex justify-center ${(!selectedElement.verticalAlign || selectedElement.verticalAlign === 'top') ? 'bg-primary/10 text-primary' : 'text-text/50 hover:bg-bg'}`}
                      >Üst</button>
                      <button
                        onClick={() => updateElement(selectedElement.id, { verticalAlign: 'middle' })}
                        className={`flex-1 p-1.5 flex justify-center border-l border-r border-borderColor ${selectedElement.verticalAlign === 'middle' ? 'bg-primary/10 text-primary' : 'text-text/50 hover:bg-bg'}`}
                      >Orta</button>
                      <button
                        onClick={() => updateElement(selectedElement.id, { verticalAlign: 'bottom' })}
                        className={`flex-1 p-1.5 flex justify-center ${selectedElement.verticalAlign === 'bottom' ? 'bg-primary/10 text-primary' : 'text-text/50 hover:bg-bg'}`}
                      >Alt</button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-bgAlt p-2 rounded border border-borderColor mt-2">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-text">Alana Sığdır (Büyüklük)</label>
                      <span className="text-[10px] text-text/50">Yazı kutuya göre küçülür</span>
                    </div>
                    <InputSwitch
                      checked={selectedElement.autoFit === true}
                      onChange={(e) => updateElement(selectedElement.id, { autoFit: e.value })}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 mt-3">
                    <label className="text-xs font-semibold text-text/70">Maksimum Satır Sayısı</label>
                    <CustomNumberInput
                      value={selectedElement.maxLines || 1}
                      onChange={(val) => updateElement(selectedElement.id, { maxLines: val || 1 })}
                      min={1}
                      max={20}
                    />
                    <span className="text-[10px] text-text/50">1'den büyükse, belirtilen genişliğe göre alt satıra geçer.</span>
                  </div>

                  {/* Text Border Properties */}
                  <div className="flex flex-col gap-1.5 mt-3 pt-3 border-t border-borderColor">
                    <label className="text-xs font-semibold text-text/70">Metin Çerçevesi Kalınlığı (px)</label>
                    <CustomNumberInput
                      value={selectedElement.borderWidth || 0}
                      onChange={(val) => updateElement(selectedElement.id, { borderWidth: val || 0 })}
                      min={0}
                      max={20}
                    />
                    <span className="text-[10px] text-text/50">0 ise çerçeve çizilmez.</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Rotation Properties for All Elements */}
          <div className="pr-4 mt-2 border-t border-borderColor pt-2">
             <div className="flex flex-col gap-1.5 mt-2">
                <label className="text-xs font-semibold text-text/70">Döndürme (Derece)</label>
                <div className="flex border border-borderColor rounded-md overflow-hidden bg-bgAlt">
                  <button
                    onClick={() => updateElement(selectedElement.id, { rotation: 0 })}
                    className={`flex-1 p-1.5 flex justify-center text-xs font-bold ${(!selectedElement.rotation) ? 'bg-primary/10 text-primary' : 'text-text/50 hover:bg-bg'}`}
                  >0°</button>
                  <button
                    onClick={() => updateElement(selectedElement.id, { rotation: 90 })}
                    className={`flex-1 p-1.5 flex justify-center text-xs font-bold border-l border-borderColor ${selectedElement.rotation === 90 ? 'bg-primary/10 text-primary' : 'text-text/50 hover:bg-bg'}`}
                  >90°</button>
                  <button
                    onClick={() => updateElement(selectedElement.id, { rotation: 180 })}
                    className={`flex-1 p-1.5 flex justify-center text-xs font-bold border-l border-borderColor ${selectedElement.rotation === 180 ? 'bg-primary/10 text-primary' : 'text-text/50 hover:bg-bg'}`}
                  >180°</button>
                  <button
                    onClick={() => updateElement(selectedElement.id, { rotation: 270 })}
                    className={`flex-1 p-1.5 flex justify-center text-xs font-bold border-l border-borderColor ${selectedElement.rotation === 270 ? 'bg-primary/10 text-primary' : 'text-text/50 hover:bg-bg'}`}
                  >270°</button>
                </div>
             </div>
          </div>

          {/* Barcode Properties */}
          {selectedElement.type === 'BARCODE' && (
            <div className="pr-4 mt-2">
              <div className="flex items-center justify-between bg-bgAlt p-2 rounded border border-borderColor">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-text">Barkod Metni (Altında)</label>
                  <span className="text-[10px] text-text/50">Yazdırıldığında altında numara çıksın</span>
                </div>
                <InputSwitch
                  checked={selectedElement.showBarcodeText !== false} // default true
                  onChange={(e) => updateElement(selectedElement.id, { showBarcodeText: e.value })}
                />
              </div>
            </div>
          )}

          {/* Shape Properties */}
          {(selectedElement.type === 'BOX' || selectedElement.type === 'LINE' || selectedElement.type === 'VLINE') && (
            <div className="pr-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-text/70">Çizgi Kalınlığı (px)</label>
                <CustomNumberInput
                  value={selectedElement.borderWidth || 1}
                  onChange={(val) => updateElement(selectedElement.id, { borderWidth: val || 1 })}


                  min={1}
                  max={20}

                />
              </div>
              <div className="flex flex-col gap-1.5 mt-2">
                <label className="text-xs font-semibold text-text/70">Çizgi Tipi</label>
                <Dropdown
                  value={selectedElement.borderStyle || 'solid'}
                  options={[
                    { label: 'Düz (Solid)', value: 'solid' },
                    { label: 'Kesikli (Dashed)', value: 'dashed' },
                    { label: 'Noktalı (Dotted)', value: 'dotted' }
                  ]}
                  onChange={(e) => updateElement(selectedElement.id, { borderStyle: e.value })}
                  className="w-full p-inputtext-sm text-sm"
                />
              </div>
            </div>
          )}

          {/* List Properties */}
          {selectedElement.type === 'LIST' && (
            <div className="pr-4 mt-2">
              <div className="flex flex-col gap-1.5 mb-3">
                <label className="text-xs font-semibold text-text/70">Veri Kaynağı (Dizi)</label>
                <input
                  type="text"
                  placeholder="Örn: items, physicalItems"
                  value={selectedElement.listConfig?.itemSource || ''}
                  onChange={(e) => updateElement(selectedElement.id, {
                    listConfig: { ...selectedElement.listConfig, itemSource: e.target.value } as any
                  })}
                  className="w-full p-2 bg-bgAlt border border-borderColor rounded text-sm text-text"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-text/70">Satır Yüks. (mm)</label>
                  <CustomNumberInput
                    value={selectedElement.listConfig?.rowHeight || 10}
                    onChange={(val) => updateElement(selectedElement.id, {
                      listConfig: { ...selectedElement.listConfig, rowHeight: val || 10 } as any
                    })}



                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-text/70">Max Y (Sayfa Sonu)</label>
                  <CustomNumberInput
                    value={selectedElement.listConfig?.maxY || 740}
                    onChange={(val) => updateElement(selectedElement.id, {
                      listConfig: { ...selectedElement.listConfig, maxY: val || 740 } as any
                    })}



                  />
                </div>
              </div>
              <button
                onClick={() => setShowListColumnsModal(true)}
                className="w-full flex items-center justify-center gap-2 p-2 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 rounded-md transition-colors text-sm font-medium"
              >
                <Settings2 className="w-4 h-4" />
                Sütunları Yönet ({selectedElement.listConfig?.columns?.length || 0})
              </button>
            </div>
          )}

          {/* Dinamik Özellikler */}
          <div className="pr-4 mt-2">
            <h3 className="text-xs font-bold text-text uppercase mb-2">Dinamik Kurallar</h3>
            <button
              onClick={() => setShowConditionsModal(true)}
              className="flex items-center justify-between w-full p-2 bg-bgAlt border border-borderColor hover:border-primary rounded transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-text/70" />
                <span className="text-sm font-medium text-text">Görünürlük Koşulları</span>
              </div>
              {selectedElement.visibleIf && selectedElement.visibleIf.length > 0 && (
                <span className="bg-primary/20 text-primary text-[10px] px-1.5 py-0.5 rounded font-bold">
                  {selectedElement.visibleIf.length} Kural
                </span>
              )}
            </button>
            <button
              onClick={() => setShowConditionalYModal(true)}
              className="flex items-center justify-between w-full p-2 bg-bgAlt border border-borderColor hover:border-primary rounded transition-colors text-left mt-2"
            >
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-text/70" />
                <span className="text-sm font-medium text-text">Koşullu Başlangıç (Y)</span>
              </div>
              {selectedElement.conditionalY && selectedElement.conditionalY.conditions.length > 0 && (
                <span className="bg-primary/20 text-primary text-[10px] px-1.5 py-0.5 rounded font-bold">
                  Aktif
                </span>
              )}
            </button>
          </div>

        </div>

        <div className="mt-6 pt-4 border-t border-borderColor flex flex-col gap-2 pr-4">
          <div className="flex gap-2">
            <button
              onClick={() => moveElementLayer?.(selectedElement.id, 'up')}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-bgAlt text-text hover:bg-borderColor rounded-md transition-colors text-xs font-semibold border border-borderColor"
              title="Öne Getir"
            >
              <ArrowUp className="w-3.5 h-3.5" />
              Öne
            </button>
            <button
              onClick={() => moveElementLayer?.(selectedElement.id, 'down')}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-bgAlt text-text hover:bg-borderColor rounded-md transition-colors text-xs font-semibold border border-borderColor"
              title="Arkaya Gönder"
            >
              <ArrowDown className="w-3.5 h-3.5" />
              Arkaya
            </button>
          </div>
          <button
            onClick={() => deleteElement(selectedElement.id)}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-danger/10 text-danger hover:bg-danger/20 rounded-md transition-colors text-sm font-bold"
          >
            <Trash2 className="w-4 h-4" />
            Nesneyi Sil
          </button>
        </div>
      </div>


      {showConditionsModal && (
        <ConditionsModal
          visible={showConditionsModal}
          onHide={() => setShowConditionsModal(false)}
          element={selectedElement}
          variables={variables}
          onSave={(conditions, logic) => updateElement(selectedElement.id, { visibleIf: conditions, visibleIfLogic: logic })}
        />
      )}

      {showListColumnsModal && (
        <ListColumnsModal
          visible={showListColumnsModal}
          onHide={() => setShowListColumnsModal(false)}
          element={selectedElement}
          onSave={(columns, itemSource, showHeader) => {
            updateElement(selectedElement.id, {
              listConfig: { ...(selectedElement.listConfig as any), columns, itemSource, showHeader }
            });
          }}
        />
      )}

      {showConditionalYModal && (
        <ConditionalYModal
          visible={showConditionalYModal}
          onHide={() => setShowConditionalYModal(false)}
          element={selectedElement}
          variables={variables}
          onSave={(conditionalY) => updateElement(selectedElement.id, { conditionalY })}
        />
      )}
    </div>
  );
};
